import { useState } from 'react';
import type { CompareResponse, CompareFilters, FilterSiteItem } from '@/api/dataVault';
import { useCompare } from '@/hooks/useDataVault';

interface Props {
  sites: FilterSiteItem[];
  selectedSites: string[];
  onClose: () => void;
}

function formatConc(val: number | null | undefined): string {
  if (val === null || val === undefined) return '--';
  if (val < 0.001) return val.toExponential(2);
  return val.toFixed(4);
}

function PctChangeBadge({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-mono-600">--</span>;
  const color = pct > 20 ? 'text-red-400' : pct < -20 ? 'text-emerald-400' : 'text-amber-400';
  const arrow = pct > 0 ? '\u2191' : pct < 0 ? '\u2193' : '\u2192';
  return <span className={`text-[11px] font-medium ${color}`}>{arrow} {pct > 0 ? '+' : ''}{pct}%</span>;
}

export function ComparePanel({ sites, selectedSites, onClose }: Props) {
  const [mode, setMode] = useState<'sites' | 'periods'>('sites');
  const [sitesA, setSitesA] = useState<string[]>([]);
  const [sitesB, setSitesB] = useState<string[]>([]);
  const [periodAFrom, setPeriodAFrom] = useState('');
  const [periodATo, setPeriodATo] = useState('');
  const [periodBFrom, setPeriodBFrom] = useState('');
  const [periodBTo, setPeriodBTo] = useState('');
  const [enabled, setEnabled] = useState(false);

  const compareFilters: CompareFilters = mode === 'sites'
    ? { mode, site_ids_a: sitesA, site_ids_b: sitesB }
    : {
        mode,
        site_ids: selectedSites,
        period_a_from: periodAFrom || undefined,
        period_a_to: periodATo || undefined,
        period_b_from: periodBFrom || undefined,
        period_b_to: periodBTo || undefined,
      };

  const isValid = mode === 'sites'
    ? sitesA.length > 0 && sitesB.length > 0
    : periodAFrom && periodATo && periodBFrom && periodBTo;

  const { data, isLoading } = useCompare(compareFilters, enabled && !!isValid);

  const handleRun = () => setEnabled(true);

  return (
    <div className="border border-mono-700 rounded-lg bg-mono-900/80 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-medium text-mono-200 tracking-widest">COMPARE MODE</h3>
        <button
          onClick={onClose}
          className="text-[10px] text-mono-500 hover:text-mono-300 tracking-wider transition-colors"
        >
          CLOSE
        </button>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1">
        {(['sites', 'periods'] as const).map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setEnabled(false); }}
            className={`px-3 py-1 text-[10px] tracking-wider rounded transition-colors ${
              mode === m ? 'bg-mono-700 text-mono-100' : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
            }`}
          >
            {m === 'sites' ? 'SITE VS SITE' : 'PERIOD VS PERIOD'}
          </button>
        ))}
      </div>

      {/* Config */}
      {mode === 'sites' ? (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[9px] text-mono-500 tracking-wider block mb-1">GROUP A</label>
            <select
              multiple
              value={sitesA}
              onChange={(e) => { setSitesA(Array.from(e.target.selectedOptions, o => o.value)); setEnabled(false); }}
              className="w-full bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 p-1.5 h-24"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[9px] text-mono-500 tracking-wider block mb-1">GROUP B</label>
            <select
              multiple
              value={sitesB}
              onChange={(e) => { setSitesB(Array.from(e.target.selectedOptions, o => o.value)); setEnabled(false); }}
              className="w-full bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 p-1.5 h-24"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-[9px] text-mono-500 tracking-wider block">PERIOD A</label>
            <div className="flex gap-2">
              <input type="date" value={periodAFrom} onChange={(e) => { setPeriodAFrom(e.target.value); setEnabled(false); }}
                className="flex-1 bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 px-2 py-1" />
              <input type="date" value={periodATo} onChange={(e) => { setPeriodATo(e.target.value); setEnabled(false); }}
                className="flex-1 bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 px-2 py-1" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[9px] text-mono-500 tracking-wider block">PERIOD B</label>
            <div className="flex gap-2">
              <input type="date" value={periodBFrom} onChange={(e) => { setPeriodBFrom(e.target.value); setEnabled(false); }}
                className="flex-1 bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 px-2 py-1" />
              <input type="date" value={periodBTo} onChange={(e) => { setPeriodBTo(e.target.value); setEnabled(false); }}
                className="flex-1 bg-mono-800 border border-mono-700 rounded text-[11px] text-mono-300 px-2 py-1" />
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleRun}
        disabled={!isValid}
        className="px-4 py-1.5 text-[10px] tracking-wider bg-mono-700 text-mono-200 hover:bg-mono-600 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        RUN COMPARISON
      </button>

      {/* Results */}
      {isLoading && (
        <div className="text-mono-500 text-[11px] tracking-wider py-4 text-center">COMPUTING COMPARISON...</div>
      )}

      {data && <CompareResults data={data} />}
    </div>
  );
}

function CompareResults({ data }: { data: CompareResponse }) {
  if (data.analytes.length === 0) {
    return <div className="text-mono-600 text-[11px] tracking-wider py-4 text-center">NO MATCHING ANALYTES</div>;
  }

  return (
    <div className="border border-mono-800 rounded overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-mono-800 bg-mono-900/50">
            <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest font-medium" rowSpan={2}>COMPOUND</th>
            <th className="px-3 py-2 text-center text-[10px] text-mono-400 tracking-widest font-medium border-l border-mono-800" colSpan={3}>{data.label_a.toUpperCase()}</th>
            <th className="px-3 py-2 text-center text-[10px] text-mono-400 tracking-widest font-medium border-l border-mono-800" colSpan={3}>{data.label_b.toUpperCase()}</th>
            <th className="px-3 py-2 text-center text-[10px] text-mono-500 tracking-widest font-medium border-l border-mono-800" rowSpan={2}>CHANGE</th>
          </tr>
          <tr className="border-b border-mono-800 bg-mono-900/50">
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal border-l border-mono-800">AVG</th>
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal">MAX</th>
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal">N</th>
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal border-l border-mono-800">AVG</th>
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal">MAX</th>
            <th className="px-2 py-1 text-right text-[9px] text-mono-600 tracking-wider font-normal">N</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-mono-800/50">
          {data.analytes.map((row) => (
            <tr key={row.analyte_code} className="hover:bg-mono-800/30 transition-colors">
              <td className="px-3 py-1.5 text-[11px] text-mono-300">{row.compound}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-300 text-right border-l border-mono-800/50">{formatConc(row.group_a.avg_concentration)}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-400 text-right">{formatConc(row.group_a.max_concentration)}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-500 text-right">{row.group_a.sample_count}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-300 text-right border-l border-mono-800/50">{formatConc(row.group_b.avg_concentration)}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-400 text-right">{formatConc(row.group_b.max_concentration)}</td>
              <td className="px-2 py-1.5 text-[11px] text-mono-500 text-right">{row.group_b.sample_count}</td>
              <td className="px-2 py-1.5 text-center border-l border-mono-800/50"><PctChangeBadge pct={row.pct_change} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
